import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import DateUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import { traceo } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import Assignation from "../models/Assignation";
import AssignationTransferForm from "../models/AssignationTransferForm";
import Position from "../models/Position";
import AssignationService from "../services/AssignationService";
import PositionService from "../services/PositionService";
import ProjectService from "../services/ProjectService";
import { QueryKeyFactory } from "../services/queryKeys";
import UserService from "../services/UserService";
import { Routes } from "../types/Routes";
import { AssignationStatusFilters } from "../utils/enums/filters/AssignationStatusFilters";
import { rethrowWithMessage, unwrap } from "../utils/queryFn";
import RoleUtils from "../utils/RoleUtils";
import { createTableQuery, refreshTableQuery, TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";
import useUpdateUrl from "./useUpdateUrl";

const assignationKeys = new QueryKeyFactory(AssignationService.path);
const positionKeys = new QueryKeyFactory(PositionService.path);
const projectKeys = new QueryKeyFactory(ProjectService.path);
const userKeys = new QueryKeyFactory(UserService.path);
const NO_NAMES: AutoCompleteUIDTO[] = [];
const DELETE_ERROR = "Error trying to delete a assignation.";

function toDeleteError(error: unknown): never {
  return rethrowWithMessage(error, DELETE_ERROR);
}

function normalizeUserNames(items: AutoCompleteUIDTO[]) {
  const normalizeLabel = (value?: string) => (value ?? "").trim().replace(/\s+/g, " ");
  const normalizeExternalId = (value?: string) => (value ?? "").trim();
  const normalizeLabelKey = (value?: string) => normalizeLabel(value).toLowerCase();
  const getNext = (current?: number) => (current ?? 0) + 1;

  const normalized = items.reduce<AutoCompleteUIDTO[]>((acc, item) => {
    const externalId = normalizeExternalId(item?.externalId);
    const label = normalizeLabel(item?.label);
    if (!externalId || !label) {
      return acc;
    }
    acc.push({ externalId, label });
    return acc;
  }, []);

  const labelCounts = new Map<string, number>();
  for (const item of normalized) {
    const key = normalizeLabelKey(item.label);
    labelCounts.set(key, getNext(labelCounts.get(key)));
  }

  const labelIndexes = new Map<string, number>();
  return normalized.map((item) => {
    const key = normalizeLabelKey(item.label);
    const count = labelCounts.get(key) ?? 0;
    if (count <= 1) {
      return item;
    }
    const nextIndex = getNext(labelIndexes.get(key));
    labelIndexes.set(key, nextIndex);
    return { ...item, label: `${item.label} (${nextIndex})` };
  });
}

const useAssignationList = () => {
  const queryClient = useQueryClient();
  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [selectedAssignation, setSelectedAssignation] = useState<Assignation | undefined>(undefined);
  const [transferAssignation, setTransferAssignation] = useState<Assignation | undefined>(undefined);
  const [assigned, setAssigned] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, false);
  const [statusFilter, setStatusFilter] = useState<string>(AssignationStatusFilters.ACTIVE);
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [positionExternalId, setPositionExternalId] = useState("");
  const [selectedPositionDetails, setSelectedPositionDetails] = useState<Position | undefined>(undefined);
  const [assignationStartDate, setAssignationStartDate] = useState(Date.now().toString());
  const [assignationEndDate, setAssignationEndDate] = useState<string>();
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO | undefined>(undefined);
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);
  const [transferProject, setTransferProject] = useState<AutoCompleteUIDTO>(EMPTY_AUTOCOMPLETE);
  const [transferPositionNames, setTransferPositionNames] = useState<AutoCompleteUIDTO[]>([]);

  const searchParams = new URLSearchParams(useLocation().search);
  const project = searchParams.get("project");
  const status = searchParams.get("status");
  const assignedToMe = searchParams.get("assigned");
  const { updateUrl } = useUpdateUrl(Routes.ASSIGNATIONS, searchParams);
  const projectExternalId = selectedProject?.externalId || "";
  const canView = RoleUtils.isAdmin() || RoleUtils.isManager();
  const formattedDate = DateUtils.formatDate(new Date(Number(assignationStartDate)), "YYYY-MM-DD");

  const projectsQuery = useQuery({
    queryKey: projectKeys.list({ scope: "assignation-names", onlyExternal: false, assigned }),
    queryFn: () => unwrap(ProjectService.findAllProjectsNames(false, assigned)),
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const projectsNames = projectsQuery.data ?? NO_NAMES;

  const managerProjectsQuery = useQuery({
    queryKey: projectKeys.list({ scope: "manager-names", onlyExternal: false, assigned: true }),
    queryFn: () => unwrap(ProjectService.findAllProjectsNames(false, true)),
    enabled: !RoleUtils.isAdmin(),
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const usersQuery = useQuery({
    queryKey: userKeys.list({ scope: "names" }),
    queryFn: () => unwrap(UserService.findUserNames()),
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const userNames = useMemo(() => normalizeUserNames(usersQuery.data ?? NO_NAMES), [usersQuery.data]);

  const positionNamesQuery = useQuery({
    queryKey: positionKeys.list({ scope: "names", projectExternalId, date: formattedDate }),
    queryFn: () => unwrap(PositionService.findPositionNamesByProjectId(projectExternalId, formattedDate)),
    enabled: canView && !!projectExternalId,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const managerQuery = useQuery({
    queryKey: projectKeys.detail(`manager:${projectExternalId}`),
    queryFn: async () => {
      if (RoleUtils.isAdmin()) return true;
      try {
        await unwrap(ProjectService.isUserCoreManagerFromProject(projectExternalId, AuthService.getUser()?.externalId!));
        return true;
      } catch {
        return false;
      }
    },
    enabled: !!projectExternalId,
  });
  const canEdit = RoleUtils.isAdmin() || !!managerQuery.data;

  const onDataFetch = useMemo(
    () =>
      createTableQuery<Assignation>({
        queryKey: (page, pageSize, sortBy, sortDir) =>
          assignationKeys.list({ page, pageSize, sortBy, sortDir, projectExternalId, statusFilter }),
        queryFn: (page, pageSize) => {
          if (!projectExternalId) return Promise.resolve(PagedResult.empty<Assignation>());
          return unwrap(AssignationService.findByProjectExternalId(page, pageSize, projectExternalId, statusFilter));
        },
        client: queryClient,
      }),
    [queryClient, projectExternalId, statusFilter],
  );

  const refreshTable = () => refreshTableQuery(assignationKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const clearPositionForm = () => {
    setPositionExternalId("");
    setSelectedPositionDetails(undefined);
    setAssignationEndDate("");
  };

  const deleteMutation = useMutation({
    mutationFn: async (assignation: Assignation) => {
      try {
        return await unwrap(AssignationService.delete(assignation));
      } catch (error) {
        toDeleteError(error);
      }
    },
    onSuccess: async () => {
      setSelectedAssignation(undefined);
      await refreshTable();
      NotificationService.info("Assignation deleted successfully");
    },
  });

  const createMutation = useMutation({
    mutationFn: (instance: Assignation) => unwrap(AssignationService.create(instance)),
    onSuccess: async () => {
      clearPositionForm();
      await refreshTable();
      NotificationService.info("Assignation created successfully");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (instance: Assignation) => unwrap(AssignationService.update(instance)),
    onSuccess: async () => {
      clearPositionForm();
      await refreshTable();
      NotificationService.info("Assignation updated successfully");
    },
  });

  const transferMutation = useMutation({
    mutationFn: (formData: AssignationTransferForm) => unwrap(AssignationService.transferAssignation(formData)),
    onSuccess: async (transfer) => {
      NotificationService.info(`Transfer completed: ${transfer.userFullName}`);
      setTransferAssignation(undefined);
      await queryClient.invalidateQueries({ queryKey: positionKeys.lists() });
      await refreshTable();
    },
  });

  useEffect(() => {
    if (!positionExternalId || !assignationStartDate) return;
    let cancelled = false;
    queryClient
      .fetchQuery({
        queryKey: assignationKeys.detail(`validate:${positionExternalId}:${assignationStartDate}:${assignationEndDate ?? ""}`),
        retry: false,
        queryFn: () =>
          unwrap(AssignationService.validateAssignationStartDate(positionExternalId, assignationStartDate, assignationEndDate || "")),
      })
      .then(() => {
        if (!cancelled) setDisableSaveButton(false);
      })
      .catch(() => {
        if (!cancelled) setDisableSaveButton(true);
      });
    return () => {
      cancelled = true;
    };
  }, [assignationStartDate, positionExternalId, assignationEndDate, queryClient]);

  useEffect(() => {
    if (project !== null && projectsNames.length > 0) {
      const projectFilter = projectsNames.find((p) => p.externalId === project);
      if (projectFilter) setSelectedProject(projectFilter);
    } else if (storedProject !== null && projectsNames.length > 0) {
      const projectFilter = projectsNames.find((p) => p.externalId === storedProject.externalId);
      if (projectFilter) setSelectedProject(projectFilter);
      searchParams.set("project", storedProject.externalId);
    }

    if (status !== null) {
      setStatusFilter(status);
    } else {
      searchParams.set("status", AssignationStatusFilters.ACTIVE);
    }

    if (assignedToMe !== null) {
      setAssigned(assignedToMe === "true");
    } else if (assigned !== undefined) {
      searchParams.set("assigned", assigned.toString());
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsNames]);

  useEffect(() => {
    setPositionExternalId("");
    setSelectedPositionDetails(undefined);
    if (projectExternalId && selectedProject) {
      setTransferProject(selectedProject);
    }
  }, [selectedProject, assignationStartDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (positionNamesQuery.data) {
      setTransferPositionNames(positionNamesQuery.data);
    }
  }, [positionNamesQuery.data]);

  const handleTransferProjectChange = (nextProject: AutoCompleteUIDTO) => {
    if (!nextProject.externalId) return;
    setTransferProject(nextProject);
    setTransferPositionNames([]);
    queryClient
      .fetchQuery({
        queryKey: positionKeys.list({ scope: "names", projectExternalId: nextProject.externalId, date: formattedDate }),
        queryFn: () => unwrap(PositionService.findPositionNamesByProjectId(nextProject.externalId, formattedDate)),
        staleTime: TABLE_QUERY_STALE_TIME,
      })
      .then(setTransferPositionNames)
      .catch((error) => traceo("Failed to get position names.", error, null));
  };

  const handleTransferAssignation = (formData: AssignationTransferForm) => {
    transferMutation.mutate(formData);
  };

  const loadTransferBuilder = (positionExternalId: string, assignationExternalId: string) =>
    queryClient.fetchQuery({
      queryKey: assignationKeys.detail(`transfer-builder:${positionExternalId}:${assignationExternalId}`),
      retry: false,
      queryFn: () => unwrap(AssignationService.getAssignationTransferBuilder(positionExternalId, assignationExternalId)),
    });

  const handleOnDeleteAssignation = () => {
    if (!selectedAssignation) return;
    deleteMutation.mutate(selectedAssignation);
  };

  const handleOnCreate = (instance: Assignation) => {
    if (!canEdit || selectedProject === undefined) return undefined;
    return createMutation.mutateAsync(instance).then(() => true);
  };

  const handleOnUpdate = (instance: Assignation) => {
    if (!canEdit) return undefined;
    return updateMutation.mutateAsync(instance).then(() => true);
  };

  const handleChangeAssignedProjectsByPosittion = () => {
    setAssigned(!assigned);
    setSelectedProject(EMPTY_AUTOCOMPLETE);
    searchParams.set("assigned", (!assigned).toString());
    setStoredProject(EMPTY_AUTOCOMPLETE);
    searchParams.delete("project");
    updateUrl();
  };

  const handlePositionSelection = (data?: AutoCompleteUIDTO) => {
    const externalId = data?.externalId || "";
    setPositionExternalId(externalId);
    if (!externalId) {
      setSelectedPositionDetails(undefined);
      return;
    }
    queryClient
      .fetchQuery({
        queryKey: positionKeys.detail(externalId),
        queryFn: () => unwrap(PositionService.findPositionByExternalId(externalId)),
      })
      .then(setSelectedPositionDetails)
      .catch((error) => {
        setSelectedPositionDetails(undefined);
        traceo("Failed to get position details.", error, null);
      });
  };

  const resetSelectedPosition = () => {
    setPositionExternalId("");
    setSelectedPositionDetails(undefined);
  };

  return {
    projectsNames,
    positionNames: positionNamesQuery.data ?? NO_NAMES,
    userNames,
    canEdit,
    forceUpdateOn,
    onDataFetch,
    selectedAssignation,
    transferAssignation,
    assigned,
    handleOnDeleteAssignation,
    handleChangeAssignedProjectsByPosittion,
    handleOnCreate,
    handleOnUpdate,
    setSelectedAssignation,
    setTransferAssignation,
    statusFilter,
    setStatusFilter,
    canManageProjects: false,
    isLoading: projectsQuery.isFetching || transferMutation.isPending,
    handleTransferAssignation,
    loadTransferBuilder,
    handleTransferProjectChange,
    transferPositionNames,
    transferProject,
    managerProjects: managerProjectsQuery.data ?? NO_NAMES,
    disableSaveButton,
    handlePositionSelection,
    setAssignationStartDate,
    setAssignationEndDate,
    setDisableSaveButton,
    selectedProject,
    setSelectedProject,
    storedProject,
    setStoredProject,
    selectedPositionDetails,
    resetSelectedPosition,
  };
};

export default useAssignationList;
